import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { ArrowLeft, Apple, Loader2, ShoppingBag, Utensils } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MealPlan = () => {
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const userId = localStorage.getItem('userId');
        if (!userId) {
          toast.error('Bitte absolviere zuerst das Quiz');
          navigate('/quiz');
          return;
        }

        const response = await axios.get(`${BACKEND_URL}/api/meal/plan/${userId}`);
        setPlan(response.data.plan);
      } catch (error) {
        console.error('Error fetching meal plan:', error);
        if (error.response?.status === 404) {
          toast.error('Kein Ernährungsplan gefunden. Bitte absolviere zuerst das Quiz.');
          navigate('/quiz');
        } else {
          toast.error('Fehler beim Laden des Plans');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPlan();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-teal-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-16">
            <Button
              data-testid="back-to-dashboard-btn"
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="h-10 px-4 rounded-lg hover:bg-zinc-800"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Dashboard
            </Button>
            <h1 className="text-xl font-bold">Ernährungsplan</h1>
            <div className="w-24" />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 md:px-8 max-w-5xl py-8">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-14 rounded-xl bg-teal-500/10 flex items-center justify-center text-teal-400">
              <Apple className="w-7 h-7" />
            </div>
            <div>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">Dein Ernährungsplan</h2>
              <p className="text-base text-zinc-400 mt-1">Lecker & gesund</p>
            </div>
          </div>
        </motion.div>

        {/* Shopping List */}
        {plan?.shopping_list && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8 bg-teal-500/10 border border-teal-500/20 rounded-xl p-6"
          >
            <div className="flex items-center gap-2 mb-4">
              <ShoppingBag className="w-5 h-5 text-teal-400" />
              <h3 className="text-lg font-semibold text-teal-400">Einkaufsliste</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {plan.shopping_list.map((item, index) => (
                <div key={index} className="flex items-center gap-2 text-sm text-zinc-300">
                  <span className="text-teal-400">✓</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Tips */}
        {plan?.tips && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-8 bg-purple-500/10 border border-purple-500/20 rounded-xl p-6"
          >
            <h3 className="text-lg font-semibold text-purple-400 mb-3">Ernährungstipps</h3>
            <ul className="space-y-2">
              {plan.tips.map((tip, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-zinc-300">
                  <span className="text-purple-400 mt-1">•</span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        )}

        {/* Weekly Meals */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Accordion type="single" collapsible className="space-y-4">
            {plan?.week_meals?.map((day, index) => (
              <AccordionItem
                key={index}
                value={`day-${day.day}`}
                className="bg-zinc-900/50 backdrop-blur-md border border-zinc-800/50 rounded-2xl overflow-hidden"
              >
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-zinc-800/30">
                  <div className="flex items-center justify-between w-full pr-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-teal-500/10 flex items-center justify-center text-teal-400 font-bold">
                        {day.day}
                      </div>
                      <div className="text-left">
                        <div className="text-lg font-semibold">{day.day_name}</div>
                        <div className="text-sm text-zinc-400">{day.meals?.length || 0} Mahlzeiten</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-zinc-400">
                      <Utensils className="w-4 h-4" />
                      {day.total_calories} kcal
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4 mt-4">
                    {day.meals?.map((meal, mIndex) => (
                      <div
                        key={mIndex}
                        className="bg-zinc-800/30 rounded-xl p-4"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="text-xs text-teal-400 font-medium mb-1">{meal.meal_type}</div>
                            <h4 className="font-semibold text-white">{meal.name}</h4>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-semibold text-white">{meal.calories} kcal</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-zinc-400">
                          <div>Protein: <span className="text-zinc-300">{meal.protein}</span></div>
                          <div>Carbs: <span className="text-zinc-300">{meal.carbs}</span></div>
                          <div>Fat: <span className="text-zinc-300">{meal.fat}</span></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </div>
  );
};

export default MealPlan;